import {
  APIPaginatedRequest,
  CountRequest,
  EntityAttribute,
  ExportRequest,
  getFetcherV2,
} from "shared/api/api";
import { clientV2 } from "shared/api/axios";
import { createURL } from "shared/api/utils";

import {
  AddSchemaDefinitionColumnsRequest,
  AttachmentImportFileRequest,
  AttachmentImportResponse,
  CreateSchemaImageRequest,
  CreateSchemaRecordAttachmentRequest,
  CreateSchemaRelationRequest,
  ListSchemaDefinitionsRequest,
  ListSchemaRelationsRequest,
  RecordAttachmentPresignRequest,
  RecordAttachmentPresignResponse,
  SchemaDefinition,
  SchemaDefinitionCreateRequest,
  SchemaDefinitionUpdateRequest,
  SchemaImage,
  SchemaRecordAttachmentFile,
  SchemaRecordRef,
  SchemaRelation,
  UpdateSchemaImageRequest,
} from "./types";

const SCHEMA_REGISTRY_PATH = "schemaRegistry";
const DEFINITIONS_PATH = "definitions";

export const SCHEMA_DEFINITIONS_ROUTE = createURL([
  SCHEMA_REGISTRY_PATH,
  DEFINITIONS_PATH,
]);

export const getSchemaDefinitionRequestURI = (id?: string): string => {
  if (!id) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([SCHEMA_REGISTRY_PATH, DEFINITIONS_PATH, id]),
  });
};

export const getSchemaDefinition = (id: string) =>
  getFetcherV2<SchemaDefinition>(getSchemaDefinitionRequestURI(id)).then(
    (r) => r.data ?? []
  );

export const listSchemaDataAttributesRequestURI = (args: {
  tableType: string | null;
  name: string | null;
}): string => {
  if (!args.tableType || !args.name) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([args.tableType, args.name, "attributes"]),
  });
};

export const listSchemaDataAttributes = (args: {
  tableType: string;
  name: string;
}) =>
  getFetcherV2<EntityAttribute[]>(
    listSchemaDataAttributesRequestURI(args)
  ).then((r) => r.data ?? []);

export const listSchemaDefinitionsRequestURI = (
  params: ListSchemaDefinitionsRequest,
  skipRequest?: boolean
): string | null => {
  if (skipRequest) return null;

  return clientV2.getUri({
    method: "GET",
    url: SCHEMA_DEFINITIONS_ROUTE,
    params,
  });
};

export const countSchemaDefinitionsRequestURI = (
  params: CountRequest
): string =>
  clientV2.getUri({
    method: "GET",
    url: createURL([SCHEMA_REGISTRY_PATH, DEFINITIONS_PATH, "count"]),
    params,
  });

export const listSchemaDefinitions = (
  args: ListSchemaDefinitionsRequest = {}
) =>
  getFetcherV2<SchemaDefinition[]>(
    listSchemaDefinitionsRequestURI(args) ?? ""
  ).then(({ data }) => data ?? []);

export const createSchemaDefinition = (data: SchemaDefinitionCreateRequest) =>
  clientV2.post<SchemaDefinition>(SCHEMA_DEFINITIONS_ROUTE, data);

export const addSchemaDefinitionColumns = (
  id: string,
  data: AddSchemaDefinitionColumnsRequest
) =>
  clientV2.post<SchemaDefinition>(
    createURL([SCHEMA_REGISTRY_PATH, DEFINITIONS_PATH, id, "columns"]),
    data
  );

export const updateSchemaDefinition = (
  id: string,
  data: SchemaDefinitionUpdateRequest
) =>
  clientV2.patch<SchemaDefinition>(
    createURL([SCHEMA_REGISTRY_PATH, DEFINITIONS_PATH, id]),
    data
  );

export const deleteSchemaDefinition = (id: string) =>
  clientV2.delete(createURL([SCHEMA_REGISTRY_PATH, DEFINITIONS_PATH, id]));

export const getSmartGroupingCostEstimateRequestURI = ({
  id,
  skipRequest,
}: {
  id: string;
  skipRequest?: boolean;
}): string => {
  if (skipRequest || !id) return "";

  return clientV2.getUri({
    method: "POST",
    url: createURL([
      SCHEMA_REGISTRY_PATH,
      DEFINITIONS_PATH,
      id,
      "smartGroupingConfig",
      "costEstimate",
    ]),
  });
};

const SCHEMA_RELATIONS_ROUTE = createURL([SCHEMA_REGISTRY_PATH, "relations"]);

export const listSchemaRelationsRequestURI = (
  params: ListSchemaRelationsRequest
): string =>
  clientV2.getUri({
    method: "GET",
    url: SCHEMA_RELATIONS_ROUTE,
    params,
  });

export const createSchemaRelation = (data: CreateSchemaRelationRequest) =>
  clientV2.post<SchemaRelation>(SCHEMA_RELATIONS_ROUTE, data);

export const deleteSchemaRelation = (id: string) =>
  clientV2.delete(createURL([SCHEMA_REGISTRY_PATH, "relations", id]));

export interface UploadCSVResult {
  insertedRows: number;
  deletedRows: number;
  table: string;
}

// Returned with HTTP 409 when the table already has data and overwrite was not
// requested — carries the counts needed to render the overwrite confirmation prompt.
export interface UploadCSVConfirmation {
  existingRows: number;
  incomingRows: number;
  table: string;
}

export const uploadSchemaDefinitionCSV = (
  id: string,
  file: File,
  overwrite = false
) => {
  const formData = new FormData();
  formData.append("file", file);

  return clientV2.post<UploadCSVResult>(
    createURL([SCHEMA_REGISTRY_PATH, DEFINITIONS_PATH, id, "upload-csv"]),
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      params: overwrite ? { overwrite: true } : undefined,
    }
  );
};

export const getSchemaRecordRequestURI = ({
  tableType,
  name,
  id,
  includeSrcURLs,
}: {
  tableType: string;
  name: string;
  id: string;
  // Opt in to presigned attachment source URLs. The details page sets this only
  // when the configured page has an attachment node, so pages without media
  // don't pay the presign cost. Folds into the SWR cache key.
  includeSrcURLs?: boolean;
}): string => {
  if (!tableType || !name || !id) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([tableType, name, id]),
    params: { includeSrcURLs },
  });
};

export const listSchemaRecordsRequestURI = ({
  tableType,
  name,
  skipRequest,
  ...params
}: {
  tableType: string;
  name: string;
  assetFilter?: string;
  assetType?: string;
  includeSrcURLs?: boolean;
  // Event listings skip the attachment fan-out by default; set this to get the
  // inline `attachments` payload. Non-event listings preload regardless.
  preloadAttachments?: boolean;
  skipRequest?: boolean;
} & APIPaginatedRequest): string => {
  if (skipRequest || !tableType || !name) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([tableType, name]),
    params,
  });
};

// listMatchedSchemaRecordsRequestURI hits the v2 matched-events endpoint:
//   GET /:schemaType/:schemaName/:id/matchedEvents/:otherSchemaName
// Returns rows from `otherSchemaName` whose v2__matched_event entries link to the
// source `id`. Each row carries `eventDetails` + `matchedEventDetails` maps that
// surface the fields driving the match (used for the yellow-highlight cells).
export const listMatchedSchemaRecordsRequestURI = ({
  tableType,
  name,
  id,
  otherSchemaName,
  ...params
}: {
  tableType: string;
  name: string;
  id: string;
  otherSchemaName: string;
} & APIPaginatedRequest): string => {
  if (!tableType || !name || !id || !otherSchemaName) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([tableType, name, id, "matchedEvents", otherSchemaName]),
    params,
  });
};

const getSchemaRecordsExportRequestURI = ({
  tableType,
  name,
  type = "xlsx",
  ...params
}: {
  tableType: string;
  name: string;
  assetFilter?: string;
  assetType?: string;
} & ExportRequest): string => {
  if (!tableType || !name) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([tableType, name, "export", type]),
    params,
  });
};

export const getSchemaRecordsExport = ({
  tableType,
  name,
  ...args
}: {
  tableType: string;
  name: string;
  assetFilter?: string;
  assetType?: string;
} & ExportRequest) =>
  getFetcherV2<Blob>(
    getSchemaRecordsExportRequestURI({ tableType, name, ...args }),
    {
      responseType: "blob",
    }
  );

export const listSchemaRecordsCountRequestURI = ({
  tableType,
  name,
  ...params
}: {
  tableType: string;
  name: string;
  filter?: string;
  assetFilter?: string;
  assetType?: string;
}): string => {
  if (!tableType || !name) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([tableType, name, "count"]),
    params,
  });
};

// --------
// Schema image rules (admin CRUD nested under a schema definition).
// --------

const schemaImagesPath = (definitionID: string, imageID?: string) =>
  imageID
    ? createURL([
        SCHEMA_REGISTRY_PATH,
        DEFINITIONS_PATH,
        definitionID,
        "images",
        imageID,
      ])
    : createURL([
        SCHEMA_REGISTRY_PATH,
        DEFINITIONS_PATH,
        definitionID,
        "images",
      ]);

export const listSchemaImagesRequestURI = (definitionID?: string): string => {
  if (!definitionID) return "";

  return clientV2.getUri({
    method: "GET",
    url: schemaImagesPath(definitionID),
  });
};

export const getSchemaImageRequestURI = (args: {
  definitionID?: string;
  imageID?: string;
}): string => {
  if (!args.definitionID || !args.imageID) return "";

  return clientV2.getUri({
    method: "GET",
    url: schemaImagesPath(args.definitionID, args.imageID),
  });
};

export const getSchemaImage = (definitionID: string, imageID: string) =>
  getFetcherV2<SchemaImage>(
    getSchemaImageRequestURI({ definitionID, imageID })
  );

export const createSchemaImage = (
  definitionID: string,
  data: CreateSchemaImageRequest
) => clientV2.post<SchemaImage>(schemaImagesPath(definitionID), data);

export const updateSchemaImage = (
  definitionID: string,
  imageID: string,
  data: UpdateSchemaImageRequest
) => clientV2.patch<SchemaImage>(schemaImagesPath(definitionID, imageID), data);

export const deleteSchemaImage = (definitionID: string, imageID: string) =>
  clientV2.delete(schemaImagesPath(definitionID, imageID));

// --------
// Schema record image (runtime lookup: returns the matching rule's bytes).
// --------

const getSchemaRecordImageURL = ({
  tableType,
  name,
  id,
}: {
  tableType: string;
  name: string;
  id: string;
}): string => {
  if (!tableType || !name || !id) return "";

  return clientV2.getUri({
    method: "GET",
    url: createURL([tableType, name, id, "image"]),
  });
};

export const getSchemaRecordImage = (args: {
  tableType: string;
  name: string;
  id: string;
}) =>
  getFetcherV2<Blob>(getSchemaRecordImageURL(args), {
    responseType: "blob",
    // A record without a matching image rule returns 404; the renderer falls
    // back to a placeholder, so don't surface this as a Sentry error.
    skipSentryReportStatuses: [404],
  });

// --------
// Attachments bulk import (checks rows, hands out presigned upload URLs).
// --------

export const importSchemaDefinitionAttachments = (
  id: string,
  files: AttachmentImportFileRequest[],
  timeout?: number
) =>
  clientV2.post<AttachmentImportResponse>(
    createURL([
      SCHEMA_REGISTRY_PATH,
      DEFINITIONS_PATH,
      id,
      "attachments",
      "import",
    ]),
    { files },
    { timeout }
  );

// --------
// Record attachments (presign, list, create for a single record).
// --------

const schemaRecordAttachmentsPath = (
  { tableType, name, id }: SchemaRecordRef,
  action?: string
) =>
  createURL([tableType, name, id, "attachments", ...(action ? [action] : [])]);

export const presignSchemaRecordAttachments = (
  ref: SchemaRecordRef,
  files: RecordAttachmentPresignRequest[]
) =>
  clientV2.post<RecordAttachmentPresignResponse>(
    schemaRecordAttachmentsPath(ref, "presign"),
    { files }
  );

// Filtered to the given ids: the server presigns src URLs for every file it
// returns, so an unfiltered list would sign URLs for the whole record.
export const listSchemaRecordAttachments = (
  ref: SchemaRecordRef,
  ids: string[]
) =>
  clientV2.get<{ data: SchemaRecordAttachmentFile[] }>(
    schemaRecordAttachmentsPath(ref),
    {
      params: {
        includeSrcURLs: true,
        filter: `ID=in:${ids.join("|")}`,
        limit: ids.length,
      },
    }
  );

export const createSchemaRecordAttachment = (
  ref: SchemaRecordRef,
  body: CreateSchemaRecordAttachmentRequest
) =>
  clientV2.post<SchemaRecordAttachmentFile>(
    schemaRecordAttachmentsPath(ref),
    body
  );
